<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FairDistribution from 'dashboard/components-next/AssignmentPolicy/components/FairDistribution.vue';
import DataTable from 'dashboard/components-next/AssignmentPolicy/components/DataTable.vue';
import AddDataDropdown from 'dashboard/components-next/AssignmentPolicy/components/AddDataDropdown.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  OPTIONS,
  ROUND_ROBIN,
  EARLIEST_CREATED,
  DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
} from 'dashboard/routes/dashboard/settings/assignmentPolicy/constants';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      description: '',
      assignmentOrder: ROUND_ROBIN,
      conversationPriority: EARLIEST_CREATED,
      fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
      fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
      excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
    }),
  },
  mode: {
    type: String,
    required: true,
    validator: value => ['CREATE', 'EDIT'].includes(value),
  },
  policyInboxes: {
    type: Array,
    default: () => [],
  },
  inboxList: {
    type: Array,
    default: () => [],
  },
  showInboxSection: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isInboxLoading: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'submit',
  'addInbox',
  'deleteInbox',
  'navigateToInbox',
  'cancel',
]);
// Duration limits for the stale-conversation threshold: 1 hour to 999 days (in minutes)
const MIN_EXCLUSION_MINUTES = 60;
const MAX_EXCLUSION_MINUTES = 1438560;

const { t } = useI18n();

const BASE_KEY = 'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY';

const state = reactive({
  name: '',
  description: '',
  enabled: true,
  assignmentOrder: ROUND_ROBIN,
  conversationPriority: EARLIEST_CREATED,
  fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
});

const isSubmitDisabled = computed(() => !state.name || props.isLoading);

const exclusionUnit = ref(DURATION_UNITS.DAYS);

// DurationInput works in minutes; the policy stores hours, so bridge the two
const excludeOlderThanMinutes = computed({
  get() {
    return state.excludeOlderThanHours == null
      ? null
      : state.excludeOlderThanHours * 60;
  },
  set(minutes) {
    state.excludeOlderThanHours =
      minutes == null ? null : Math.round(minutes / 60);
  },
});

const createOption = (type, key) => ({
  key,
  label: t(`${BASE_KEY}.FORM.${type}.${key.toUpperCase()}.LABEL`),
  description: t(`${BASE_KEY}.FORM.${type}.${key.toUpperCase()}.DESCRIPTION`),
});

const assignmentOrderOptions = computed(() =>
  OPTIONS.ORDER.map(key => createOption('ASSIGNMENT_ORDER', key))
);

const assignmentPriorityOptions = computed(() =>
  OPTIONS.PRIORITY.map(key => createOption('ASSIGNMENT_PRIORITY', key))
);

const radioSections = computed(() => [
  {
    key: 'assignmentOrder',
    label: t(`${BASE_KEY}.FORM.ASSIGNMENT_ORDER.LABEL`),
    options: assignmentOrderOptions.value,
  },
  {
    key: 'conversationPriority',
    label: t(`${BASE_KEY}.FORM.ASSIGNMENT_PRIORITY.LABEL`),
    options: assignmentPriorityOptions.value,
  },
]);

const buttonLabel = computed(() =>
  t(`${BASE_KEY}.${props.mode.toUpperCase()}.${props.mode}_BUTTON`)
);

const resetForm = () => {
  Object.assign(state, {
    name: '',
    description: '',
    enabled: true,
    assignmentOrder: ROUND_ROBIN,
    conversationPriority: EARLIEST_CREATED,
    fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
    fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
    excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
  });
};

const handleSubmit = () => {
  emit('submit', { ...state });
};

// Pick the display unit from the stored value so non-day thresholds (e.g. 25h) don't get floored
const detectExclusionUnit = hours => {
  exclusionUnit.value =
    hours && hours % 24 !== 0 ? DURATION_UNITS.HOURS : DURATION_UNITS.DAYS;
};

watch(
  () => props.initialData,
  newData => {
    Object.assign(state, newData);
    detectExclusionUnit(newData.excludeOlderThanHours);
  },
  { immediate: true, deep: true }
);

defineExpose({
  resetForm,
});
</script>

<template>
  <form class="flex flex-col w-full" @submit.prevent="handleSubmit">
    <!-- Name -->
    <div class="w-full mb-4">
      <label>
        {{ t(`${BASE_KEY}.FORM.NAME.LABEL`) }}
        <input
          v-model.trim="state.name"
          type="text"
          :placeholder="t(`${BASE_KEY}.FORM.NAME.PLACEHOLDER`)"
        />
      </label>
    </div>

    <!-- Description -->
    <div class="w-full mb-4">
      <label>
        {{ t(`${BASE_KEY}.FORM.DESCRIPTION.LABEL`) }}
        <textarea
          v-model="state.description"
          :rows="3"
          :placeholder="t(`${BASE_KEY}.FORM.DESCRIPTION.PLACEHOLDER`)"
        />
      </label>
    </div>

    <!-- Radio Sections (Order & Priority) -->
    <div
      v-for="section in radioSections"
      :key="section.key"
      class="w-full mb-6"
    >
      <label>
        {{ section.label }}
      </label>
      <div class="flex flex-col gap-2.5 mt-2">
        <div
          v-for="option in section.options"
          :key="option.key"
          class="flex items-start"
        >
          <input
            :id="option.key"
            v-model="state[section.key]"
            type="radio"
            :value="option.key"
            class="ltr:mr-2 rtl:ml-2 mt-1 shrink-0 cursor-pointer accent-n-brand text-n-brand"
          />
          <label
            :for="option.key"
            class="text-sm font-normal flex-1 cursor-pointer !mb-0"
          >
            <span class="block">
              {{ option.label }}
            </span>
            <span class="block text-xs text-n-slate-11">
              {{ option.description }}
            </span>
          </label>
        </div>
      </div>
    </div>

    <div class="w-full mb-6">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t(`${BASE_KEY}.FORM.EXCLUDE_OLDER_THAN.LABEL`) }}
      </label>
      <p class="mb-4 text-xs text-n-slate-11">
        {{ t(`${BASE_KEY}.FORM.EXCLUDE_OLDER_THAN.DESCRIPTION`) }}
      </p>
      <div
        class="flex items-center gap-2 [&>select]:!bg-n-alpha-2 [&>select]:!outline-none [&>select]:hover:brightness-110"
      >
        <DurationInput
          v-model:unit="exclusionUnit"
          v-model:model-value="excludeOlderThanMinutes"
          :min="MIN_EXCLUSION_MINUTES"
          :max="MAX_EXCLUSION_MINUTES"
        />
      </div>
    </div>

    <!-- Fair Distribution -->
    <div class="w-full mb-6">
      <label>
        {{ t(`${BASE_KEY}.FORM.FAIR_DISTRIBUTION.LABEL`) }}
      </label>
      <p class="text-xs text-n-slate-11 -mt-2 mb-4">
        {{ t(`${BASE_KEY}.FORM.FAIR_DISTRIBUTION.DESCRIPTION`) }}
      </p>
      <FairDistribution
        v-model:fair-distribution-limit="state.fairDistributionLimit"
        v-model:fair-distribution-window="state.fairDistributionWindow"
        v-model:window-unit="state.windowUnit"
      />
    </div>

    <!-- Actions -->
    <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
      <Button
        faded
        slate
        type="reset"
        :label="
          t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.CANCEL_BUTTON_LABEL'
          )
        "
        @click.prevent="emit('cancel')"
      />
      <Button
        type="submit"
        :label="buttonLabel"
        :disabled="isSubmitDisabled"
        :is-loading="isLoading"
      />
    </div>

    <!-- Inboxes Section -->
    <div
      v-if="showInboxSection"
      class="py-4 flex-col flex gap-4 border-t border-n-weak mt-6"
    >
      <div class="flex items-end gap-4 w-full justify-between">
        <div class="flex flex-col items-start gap-1 py-1">
          <label class="text-sm font-medium text-n-slate-12 py-1">
            {{ t(`${BASE_KEY}.FORM.INBOXES.LABEL`) }}
          </label>
          <p class="mb-0 text-n-slate-11 text-sm">
            {{ t(`${BASE_KEY}.FORM.INBOXES.DESCRIPTION`) }}
          </p>
        </div>
        <AddDataDropdown
          :label="t(`${BASE_KEY}.FORM.INBOXES.ADD_BUTTON`)"
          :search-placeholder="
            t(`${BASE_KEY}.FORM.INBOXES.DROPDOWN.SEARCH_PLACEHOLDER`)
          "
          :items="inboxList"
          @add="$emit('addInbox', $event)"
        />
      </div>
      <DataTable
        :items="policyInboxes"
        :is-fetching="isInboxLoading"
        :empty-state-message="t(`${BASE_KEY}.FORM.INBOXES.EMPTY_STATE`)"
        @delete="$emit('deleteInbox', $event)"
        @navigate="$emit('navigateToInbox', $event)"
      />
    </div>
  </form>
</template>
